import { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

const ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QTY: 'UPDATE_QTY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART',
};

function cartReducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOAD_CART:
      return { ...state, items: action.payload };

    case ACTIONS.ADD_ITEM: {
      const existing = state.items.find((i) => i._id === action.payload._id);
      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            i._id === action.payload._id ? { ...i, quantity: i.quantity + 1 } : i
          ),
        };
      }
      return { ...state, items: [...state.items, { ...action.payload, quantity: 1 }] };
    }

    case ACTIONS.REMOVE_ITEM:
      return { ...state, items: state.items.filter((i) => i._id !== action.payload) };

    case ACTIONS.UPDATE_QTY:
      if (action.payload.quantity <= 0) {
        return { ...state, items: state.items.filter((i) => i._id !== action.payload._id) };
      }
      return {
        ...state,
        items: state.items.map((i) =>
          i._id === action.payload._id ? { ...i, quantity: action.payload.quantity } : i
        ),
      };

    case ACTIONS.CLEAR_CART:
      return { ...state, items: [] };

    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('gramaayam_cart');
      if (saved) dispatch({ type: ACTIONS.LOAD_CART, payload: JSON.parse(saved) });
    } catch (e) { /* ignore */ }
  }, []);

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem('gramaayam_cart', JSON.stringify(state.items));
  }, [state.items]);

  const addToCart = (product) => dispatch({ type: ACTIONS.ADD_ITEM, payload: product });
  const removeFromCart = (id) => dispatch({ type: ACTIONS.REMOVE_ITEM, payload: id });
  const updateQuantity = (_id, quantity) =>
    dispatch({ type: ACTIONS.UPDATE_QTY, payload: { _id, quantity } });
  const clearCart = () => dispatch({ type: ACTIONS.CLEAR_CART });

  const cartCount = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = state.items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const shippingCharge = subtotal >= 500 ? 0 : 49;
  const total = subtotal + shippingCharge;

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        subtotal,
        shippingCharge,
        total,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
